/**
 * Splits a list into `columnCount` columns for a fixed CSS grid track (`grid-cols-N` +
 * `items-start`), so each cell sizes to its own content instead of the CSS grid default of
 * stretching every cell in a row to match its tallest neighbor.
 *
 * Uses longest-processing-time-first bin packing: items are assigned heaviest-first, each to
 * whichever column currently has the lowest total weight, then each column is put back in its
 * items' original relative order for a stable, predictable read order. Assigning heaviest-first
 * (rather than in list order) matters once weights are uneven — a single large item early in the
 * list would otherwise anchor one column before the packer has seen how big everything else is,
 * skewing the split (e.g. `[10, 1, 1, 1, 1, 1]` in list order dumps all five 1s on the other
 * column; heaviest-first still isolates the 10 but the two are much closer at 10 vs 5).
 *
 * Weight is a static estimate (e.g. field count), never the live rendered DOM height — a
 * browser-balanced CSS multi-column layout uses real height and re-splits (visibly reshuffling
 * cards) whenever any card's height changes, e.g. an accordion opening or a search filtering the
 * list. With the default weight (1 per item), this still degenerates to plain round-robin.
 *
 * @param {any[]} items - Items to distribute.
 * @param {number} [columnCount=2] - Number of columns to split into. Must be at least 1.
 * @param {(item: any) => number} [weightOf] - Estimated relative size of one item (e.g. its field
 *   count), used only to balance columns — never measured from the live DOM.
 * @returns {any[][]} One array per column, each in the items' original relative order.
 * @example
 * toColumns(["a", "b", "c", "d", "e"], 2); // => [["a", "c", "e"], ["b", "d"]]
 * @example
 * toColumns([{ w: 5 }, { w: 1 }, { w: 1 }], 2, item => item.w);
 * // => [[{ w: 5 }], [{ w: 1 }, { w: 1 }]]
 */
export const toColumns = (items, columnCount = 2, weightOf = () => 1) => {
  if (columnCount < 1)
    throw new Error(`toColumns: columnCount must be at least 1, got ${columnCount}`);

  const entries = items.map((item, index) => ({ item, index, weight: weightOf(item) }));
  const heaviestFirst = [...entries].sort((a, b) => b.weight - a.weight);

  const columns = Array.from({ length: columnCount }, () => []);
  const columnWeights = Array(columnCount).fill(0);

  heaviestFirst.forEach(entry => {
    const lightestColumn = columnWeights.indexOf(Math.min(...columnWeights));
    columns[lightestColumn].push(entry);
    columnWeights[lightestColumn] += entry.weight;
  });

  return columns.map(column => column.sort((a, b) => a.index - b.index).map(({ item }) => item));
};
